"use client";

import { useContext, useEffect, useRef, useState } from "react";
import { useRouter } from "@bprogress/next";
import { ArrowRightIcon, ListChecksIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SuperLink } from "@/components/common/super-link";
import { PictureAsset } from "@/components/common/picture-asset";
import { CountdownTimer } from "@/components/common/countdown-timer";
import { PracticeExamParticipantsSummary } from "@/components/exams/practice-exam/practice-exam-banner-shell";
import { getTimeForExam } from "@/components/exams/exam-question/components/exam-timer/manual-exam-timer/service";
import {
  formatPracticeRoundStart,
  formatPracticeRoundTime,
} from "@/service/exams/practice-exam-info";
import { clientPost } from "@/service/client-api/client-api";
import { createPath } from "@/service/routing/routing";
import { logEvent } from "@/service/logs/log";
import { ResourceContext } from "@/contexts/resource-context";
import { getAssetImage } from "@/service/image/asset";
import { cn } from "@/lib/utils";

const REFRESH_COOLDOWN = 10_000;

/**
 * Karta jednej rundy próbnego LEK-u. Cztery stany z projektu: zdana (wynik + czas), przegapiona
 * (runda zamknięta), trwa (uczestnicy + odliczanie + „Rozpocznij") i wkrótce (data startu).
 * Grafika rundy jest na razie pustym placeholderem (jak w karcie zestawu) — url doklei się później.
 */
export const PracticeExamRoundCard = ({
  round,
  isNext = false,
}: {
  round: ExamPracticeExamRoundVM;
  // najbliższa zbliżająca się runda — nie wyszarzamy jej mimo stanu „wkrótce"
  isNext?: boolean;
}) => {
  const router = useRouter();
  const { logView } = useContext(ResourceContext);
  const lastRefreshRef = useRef(0);

  const isActive = "active" === round.state;
  const isMissed = "missed" === round.state;
  const isPassed = "passed" === round.state;
  const isUpcoming = "upcoming" === round.state;

  // wyszarzamy tylko dalsze rundy „wkrótce" — najbliższa zbliżająca się oraz rundy, które już się
  // odbyły (zdana / przegapiona / trwa), zostają w kolorze
  const isDimmed = isUpcoming && !isNext;

  const [timeLeft, setTimeLeft] = useState(
    getTimeForExam(new Date(round.endDate)),
  );

  useEffect(() => {
    if (!isActive) return;

    const tick = () => {
      const next = getTimeForExam(new Date(round.endDate));

      const totalSecondsLeft =
        (next.days ?? 0) * 86400 +
        next.hours * 3600 +
        next.minutes * 60 +
        next.seconds;

      // koniec rundy — przeładowanie danych (z cooldownem, żeby nie zapętlić)
      if (totalSecondsLeft <= 0) {
        const now = Date.now();
        if (now - lastRefreshRef.current > REFRESH_COOLDOWN) {
          lastRefreshRef.current = now;
          router.refresh();
        }
        return;
      }

      setTimeLeft(next);
    };

    const intervalId = setInterval(tick, 1000);

    return () => clearInterval(intervalId);
  }, [isActive, round.endDate, router]);

  async function startAttempt() {
    const { id } = await clientPost("exam.start_practice_attempt");
    console.log(id);

    logEvent("practice_exam_cta_start_clicked", logView);

    window.location.replace(
      createPath("exam.attempt.question", { attempt: id, questionNumber: 1 }),
    );
  }

  const resultHref = round.attemptId
    ? createPath("exam.attempt.result", { attempt: round.attemptId })
    : null;

  const badge = (() => {
    if (isPassed) {
      return (
        <Badge
          className={cn(
            "border-transparent",
            round.passed
              ? "bg-teal-50 text-teal-600"
              : "bg-rose-50 text-rose-600",
          )}
        >
          {round.passed ? "zdana" : "niezdana"} {round.score}%
        </Badge>
      );
    }

    if (isMissed) {
      return (
        <Badge className="border-transparent bg-zinc-100 text-zinc-600">
          przegapiona
        </Badge>
      );
    }

    if (isActive) {
      return <Badge variant={"brandSoft"}>trwa</Badge>;
    }

    return (
      <Badge className="border-transparent bg-orange-50 text-orange-600">
        wkrótce
      </Badge>
    );
  })();

  const subtext = (() => {
    if (isPassed) {
      return (
        <p className="text-muted-foreground text-sm">
          Czas: {formatPracticeRoundTime(round.timeSpent ?? 0)}
        </p>
      );
    }

    if (isMissed) {
      return <p className="text-muted-foreground text-sm">Runda zamknięta</p>;
    }

    if (isActive) {
      return (
        <PracticeExamParticipantsSummary
          participantCount={round.participantCount}
          participantInitials={round.participantInitials}
          isRoundActive
        />
      );
    }

    return (
      <p className="text-muted-foreground hidden text-sm lg:block">
        {formatPracticeRoundStart(round.startDate)}
      </p>
    );
  })();

  const cta = (() => {
    if (isPassed || isMissed) {
      // przegapionej rundy nie ma jak podejrzeć — przycisk zostaje, ale wyłączony i wyszarzony
      if (isMissed || !resultHref) {
        return (
          <Button
            variant="outline"
            className={cn("w-full", isMissed && "grayscale")}
            disabled
          >
            Zobacz wynik
          </Button>
        );
      }

      return (
        <Button variant="outline" className="w-full" asChild>
          <SuperLink href={resultHref}>Zobacz wynik</SuperLink>
        </Button>
      );
    }

    if (isActive) {
      // podejście już oddane w tej rundzie → wynik, w przeciwnym razie start
      if (round.isFinishedByUser && resultHref) {
        return (
          <Button variant="outline" className="w-full" asChild>
            <SuperLink href={resultHref}>
              <ListChecksIcon /> Zobacz wynik
            </SuperLink>
          </Button>
        );
      }

      return (
        <Button variant="orange" className="w-full" onClick={startAttempt}>
          Rozpocznij <ArrowRightIcon />
        </Button>
      );
    }

    // wkrótce — rundy jeszcze nie można rozpocząć; dalsze rundy mają też wyszarzony przycisk
    return (
      <Button variant="orange" className={cn("w-full grayscale")} disabled>
        Rozpocznij <ArrowRightIcon />
      </Button>
    );
  })();

  return (
    <div className="flex w-[340px] shrink-0 flex-col gap-4 rounded-lg border border-zinc-100 bg-white p-3 shadow-[4px_8px_19px_0px_rgba(92,92,92,0.06)]">
      <div className="flex w-full flex-col gap-2">
        {/* Grafika rundy — pusty placeholder (url doklei się później) */}
        <div
          className={cn(
            "relative h-[105px] w-full overflow-hidden rounded-[5px] bg-zinc-100",
            isDimmed && "opacity-80",
          )}
        >
          <PictureAsset
            source={getAssetImage(
              `/exams/illustrations/probny-runda-${round.number}`,
            )}
            ext="png"
            alt=""
            wrapperClassName="pointer-events-none absolute inset-0"
            className={cn(
              "h-full w-full object-cover",
              // rundy bez akcji (przegapiona / wkrótce) mają wyszarzoną grafikę
              isDimmed && "grayscale",
            )}
          />
          {isDimmed && (
            <span aria-hidden className="absolute inset-0 bg-black/10" />
          )}
          {/* przy otwartej rundzie licznik do zamknięcia siedzi na grafice, jak w projekcie */}
          {isActive && (
            <div className="absolute right-2 bottom-2">
              <CountdownTimer
                timeLeft={timeLeft}
                isActive
                variant="practiceExam"
                // dwukropki biorą kolor z korzenia — na grafice mają być białe
                className="text-white"
              />
            </div>
          )}
        </div>

        {/* Tytuł + status */}
        <div className="flex w-full flex-col">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-base font-semibold text-zinc-950">
              Runda {round.number}
            </h3>
            {badge}
          </div>
          {subtext}
        </div>
      </div>

      {cta}
    </div>
  );
};
